import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, CardContent, Button, CircularProgress, Alert, Chip, Tab, Tabs } from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import AddIcon from '@mui/icons-material/Add';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import { api } from '../api/client';
import { useDecodedToken } from '../utils/useDecodedToken';
import { useNavigate } from 'react-router-dom';
import PaymentDialog from '../components/payments/PaymentDialog';
import UserTypeIndicator from '../components/common/UserTypeIndicator';

interface Loan {
    id: number;
    loanNumber: string;
    status: string;
    loanType: string;
    amount: number;
    interestRate: number;
    termMonths: number;
    monthlyPayment: number;
    disbursedAmount: number;
    outstandingBalance: number;
    purpose: string;
    applicationDate: string;
    nextPaymentDate: string;
}

const Loans: React.FC = () => {
    const user = useDecodedToken();
    const navigate = useNavigate();
    const [loans, setLoans] = useState<Loan[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [tab, setTab] = useState(0);
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [selectedLoan, setSelectedLoan] = useState<any | null>(null);

    useEffect(() => {
        fetchLoans();
    }, [user]);

    const fetchLoans = async () => {
        if (!user) return;
        
        setLoading(true);
        setError('');
        try {
            let response;
            
            // Different API calls based on user type
            if (user.registrationType === 'existing_customer') {
                // For existing customers, get loans from their client account
                response = await api.get(`/api/clients/${user.accountNumber}/accounts`);
                setLoans(response.data.loanAccounts || []);
            } else if (user.registrationType === 'loan_applicant' && user.trackingNumber) {
                // For loan applicants, track their specific loan application
                response = await api.get(`/api/v1/client-loans/track/${user.trackingNumber}`);
                setLoans(response.data ? [response.data] : []);
            } else {
                // For new users or loan applicants, get their loan applications
                response = await api.get(`/api/v1/client-loans/user/${user.sub}`);
                setLoans(response.data || []);
            }
        } catch (err: any) {
            console.error('Failed to fetch loans:', err);
            setError(err.response?.data?.message || 'Failed to load loans. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleApplyLoan = () => {
        navigate('/loan-application');
    };

    const getOutstandingBalance = (loan: any): number => {
        return Number(
            loan.outstandingBalance ??
            loan.totalOutstanding ??
            loan.summary?.totalOutstanding ??
            loan.summary?.principalOutstanding ??
            0
        );
    };

    const handleMakePayment = (loan: any) => {
        setSelectedLoan(loan);
        setPaymentDialogOpen(true);
    };

    const handlePaymentDialogClose = () => {
        setPaymentDialogOpen(false);
        setSelectedLoan(null);
    };

    const handleViewLoan = (loan: any) => {
        if (loan?.id) {
            navigate(`/loan/${loan.id}`);
        }
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString();
    };

    const getStatusColor = (status: string) => {
        switch (status.toUpperCase()) {
            case 'ACTIVE':
                return 'success.main';
            case 'PENDING':
                return 'warning.main';
            case 'REJECTED':
                return 'error.main';
            default:
                return 'text.primary';
        }
    };

    const getStatusChipColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
        switch ((status || '').toLowerCase()) {
            case 'active':
            case 'approved':
            case 'disbursed':
                return 'success';
            case 'pending':
            case 'submitted':
                return 'warning';
            case 'rejected':
            case 'cancelled':
                return 'error';
            case 'in review':
            case 'under assessment':
                return 'info';
            default:
                return 'default';
        }
    };

    const formatLoanAmount = (loan: any) => {
        const amount = loan.amount || loan.requestedAmount || loan.principalAmount || loan.disbursedAmount || 0;
        const currency = loan.currency?.code || 'USD';
        return `${currency} ${amount.toLocaleString()}`;
    };

    const formatApplicationDate = (loan: any) => {
        let dateArray = loan.applicationDate || loan.submittedOnDate || loan.timeline?.submittedOnDate;
        
        if (Array.isArray(dateArray) && dateArray.length >= 3) {
            // Convert array format [2024, 1, 15] to Date
            return new Date(dateArray[0], dateArray[1] - 1, dateArray[2]).toLocaleDateString();
        } else if (typeof dateArray === 'string') {
            return formatDate(dateArray);
        }
        return 'N/A';
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const canApplyForLoans = user?.registrationType === 'new_user' || user?.registrationType === 'loan_applicant';
    const isExistingCustomer = user?.registrationType === 'existing_customer';

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4">
                    {isExistingCustomer ? 'My Loans' : 'Loan Applications'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <UserTypeIndicator />
                    {canApplyForLoans && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={handleApplyLoan}
                        >
                            {user?.registrationType === 'new_user' ? 'Apply for Loan' : 'New Application'}
                        </Button>
                    )}
                </Box>
            </Box>

            {user?.registrationType === 'loan_applicant' && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Tracking loan application with number: {user.trackingNumber}
                    </Typography>
                    <Typography variant="body2">
                        Full account features will be available once your loan is approved and account is created.
                    </Typography>
                </Alert>
            )}

            {user?.registrationType === 'new_user' && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Welcome to SMEDCO!
                    </Typography>
                    <Typography variant="body2">
                        You can create and track loan applications. Full account features will be available once your loan is approved.
                    </Typography>
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {loans.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                    <TrackChangesIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        {isExistingCustomer ? 'No loans found' : 'No loan applications found'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {canApplyForLoans 
                            ? 'Get started by creating your first loan application.'
                            : 'Contact SMEDCO to apply for a loan.'
                        }
                    </Typography>
                    {canApplyForLoans && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={handleApplyLoan}
                        >
                            Apply for Loan
                        </Button>
                    )}
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {loans.map((loan: any) => (
                        <Grid item xs={12} md={6} key={loan.id || loan.trackingNumber}>
                            <Card elevation={3}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Box>
                                            <Typography variant="h6">
                                                {isExistingCustomer 
                                                    ? `Loan #${loan.loanNumber || loan.accountNo}` 
                                                    : `Application #${loan.trackingNumber || loan.id}`
                                                }
                                            </Typography>
                                            {loan.trackingNumber && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Tracking: {loan.trackingNumber}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Chip
                                            label={loan.status?.value || loan.status || 'Pending'}
                                            color={getStatusChipColor(loan.status?.value || loan.status)}
                                            size="small"
                                        />
                                    </Box>
                                    
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" color="text.secondary">
                                                {isExistingCustomer ? 'Product' : 'Loan Type'}
                                            </Typography>
                                            <Typography variant="body1">
                                                {loan.productName || loan.loanType || loan.loanProductName || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" color="text.secondary">
                                                Amount
                                            </Typography>
                                            <Typography variant="body1">
                                                {formatLoanAmount(loan)}
                                            </Typography>
                                        </Grid>
                                        
                                        {isExistingCustomer && (
                                            <>
                                                <Grid item xs={6}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Outstanding Balance
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {formatCurrency(loan.outstandingBalance || loan.totalOutstanding || 0)}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Currency
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {loan.currency?.code || 'USD'}
                                                    </Typography>
                                                </Grid>
                                            </>
                                        )}
                                        
                                        <Grid item xs={6}>
                                            <Typography variant="body2" color="text.secondary">
                                                {isExistingCustomer ? 'Approval Date' : 'Application Date'}
                                            </Typography>
                                            <Typography variant="body1">
                                                {formatApplicationDate(loan)}
                                            </Typography>
                                        </Grid>
                                        
                                        {loan.purpose && (
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Purpose
                                                </Typography>
                                                <Typography variant="body1">
                                                    {loan.purpose}
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                    
                                    {/* Action buttons based on user type and loan status */}
                                    <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                                        {isExistingCustomer && (
                                            <>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => handleViewLoan(loan)}
                                                >
                                                    View Details
                                                </Button>
                                                {(loan.status?.value === 'Active' || String(loan.status || '').toLowerCase() === 'active') && (
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={<PaymentIcon />}
                                                        onClick={() => handleMakePayment(loan)}
                                                        disabled={getOutstandingBalance(loan) <= 0}
                                                    >
                                                        Make Payment
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                        {!isExistingCustomer && (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<TrackChangesIcon />}
                                                onClick={() => navigate('/loan-applications')}
                                            >
                                                Track Status
                                            </Button>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {selectedLoan && (
                <PaymentDialog
                    open={paymentDialogOpen}
                    onClose={handlePaymentDialogClose}
                    loanId={Number(selectedLoan.id)}
                    loanNumber={selectedLoan.loanNumber || selectedLoan.accountNo || String(selectedLoan.id)}
                    outstandingBalance={getOutstandingBalance(selectedLoan)}
                    loanCurrency={selectedLoan.currency?.code || selectedLoan.currency || 'USD'}
                    onPaymentInitiated={() => {
                        handlePaymentDialogClose();
                        fetchLoans();
                    }}
                />
            )}
        </Box>
    );
};

export default Loans; 